import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Play, Pause, SkipForward, SkipBack, Loader2, ChevronRight, RefreshCw,
  CheckCircle, X, Volume2, VolumeX, Mail, Phone
} from "lucide-react";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Public, unauthenticated video viewer for commercials/prospects.
 * URL: /v/:serviceId  (short, shareable, QR-friendly)
 */
export default function PublicVideoViewer() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [storyboard, setStoryboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [muted, setMuted] = useState(false);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/public/videos/${serviceId}`);
        setStoryboard(res.data);
      } catch (e) {
        setError(e.response?.status === 404
          ? "Cette vidéo n'est pas disponible (lien incorrect ou vidéo en préparation)."
          : "Erreur de chargement");
      } finally {
        setLoading(false);
      }
    })();
  }, [serviceId]);

  // Play audio narration when scene changes
  useEffect(() => {
    if (!storyboard || !started || !playing || finished) return;
    const current = storyboard.scenes[currentIdx];
    if (!current || !audioRef.current) return;
    audioRef.current.src = `${API}/public/videos/audio/${serviceId}/${current.id}`;
    audioRef.current.muted = muted;
    audioRef.current.play().catch(() => {/* no audio file or autoplay blocked */});
    return () => {
      try { audioRef.current?.pause(); } catch {}
    };
  }, [storyboard, currentIdx, started, playing, finished, muted, serviceId]);

  // Pause audio when video is paused
  useEffect(() => {
    if (!audioRef.current) return;
    if (!playing || finished) {
      audioRef.current.pause();
    } else if (started) {
      audioRef.current.play().catch(() => {});
    }
  }, [playing, finished, started]);

  useEffect(() => {
    if (!storyboard || !playing || finished || !started) return;
    const current = storyboard.scenes[currentIdx];
    if (!current) return;
    timerRef.current = setTimeout(() => {
      if (currentIdx < storyboard.scenes.length - 1) {
        setCurrentIdx(i => i + 1);
      } else {
        setFinished(true);
        setPlaying(false);
      }
    }, current.duration_ms || 5500);
    return () => clearTimeout(timerRef.current);
  }, [storyboard, currentIdx, playing, finished, started]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-amber-400" />
      </div>
    );
  }

  if (error || !storyboard) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-black flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
            <X className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">Vidéo indisponible</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <Button onClick={() => navigate("/")} className="bg-amber-500 hover:bg-amber-400 text-slate-950">
            Découvrir CMES Pharma Intel
          </Button>
        </div>
      </div>
    );
  }

  const scene = storyboard.scenes[currentIdx];
  const progress = ((currentIdx + 1) / storyboard.scenes.length) * 100;
  const themeColor = storyboard.color_theme;

  return (
    <div className="fixed inset-0 bg-black flex flex-col select-none" data-testid="public-video-viewer">
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/80 to-transparent">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center font-bold text-slate-950 text-xs">
              CMES
            </div>
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-widest">{storyboard.title}</p>
              <p className="text-sm text-white font-semibold">{storyboard.tagline}</p>
            </div>
          </div>
        </div>
        {started && (
          <div className="px-6 pb-3">
            <div className="h-1 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full transition-all duration-300" style={{ width: `${progress}%`, background: themeColor }} />
            </div>
            <div className="flex justify-between text-[10px] text-slate-500 mt-1 uppercase tracking-wider">
              <span>Scène {currentIdx + 1} / {storyboard.scenes.length}</span>
              <span>{Math.round(progress)}%</span>
            </div>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 relative overflow-hidden">
        {!started ? (
          <StartScreen storyboard={storyboard} onStart={() => { setStarted(true); setPlaying(true); }} />
        ) : finished ? (
          <FinalCta
            storyboard={storyboard}
            onRestart={() => { setCurrentIdx(0); setFinished(false); setPlaying(true); }}
          />
        ) : (
          <SceneFrame key={scene.id} serviceId={serviceId} scene={scene} themeColor={themeColor} isPublic />
        )}
      </div>

      {/* Bottom controls (only when started, not finished) */}
      {started && !finished && (
        <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black/90 to-transparent pt-12 pb-6 px-6">
          <div className="flex items-center justify-center gap-4">
            <button onClick={() => currentIdx > 0 && setCurrentIdx(i => i - 1)}
              disabled={currentIdx === 0}
              className="text-white hover:bg-white/10 p-3 rounded-full disabled:opacity-30">
              <SkipBack className="w-5 h-5" />
            </button>
            <button onClick={() => setPlaying(p => !p)} className="text-slate-950 bg-white hover:bg-amber-300 p-4 rounded-full shadow-lg">
              {playing ? <Pause className="w-6 h-6 fill-current" /> : <Play className="w-6 h-6 fill-current" />}
            </button>
            <button onClick={() => currentIdx < storyboard.scenes.length - 1 && setCurrentIdx(i => i + 1)}
              disabled={currentIdx === storyboard.scenes.length - 1}
              className="text-white hover:bg-white/10 p-3 rounded-full disabled:opacity-30">
              <SkipForward className="w-5 h-5" />
            </button>
            <button onClick={() => setMuted(m => !m)}
              data-testid="viewer-mute-btn"
              title={muted ? "Activer la voix off" : "Couper la voix off"}
              className="text-white hover:bg-white/10 p-3 rounded-full ml-2">
              {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
          </div>
          {/* Hidden audio element for narration voice-over */}
          <audio ref={audioRef} preload="auto" playsInline />
          <div className="flex items-center justify-center gap-1.5 mt-4">
            {storyboard.scenes.map((s, i) => (
              <button key={s.id} onClick={() => { setCurrentIdx(i); setFinished(false); }}
                className={`h-1.5 rounded-full transition-all ${i === currentIdx ? "w-8 bg-white" : "w-1.5 bg-white/30 hover:bg-white/50"}`} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StartScreen({ storyboard, onStart }) {
  const themeColor = storyboard.color_theme;
  return (
    <div className="absolute inset-0 flex items-center justify-center p-8 animate-fade-in"
      style={{ background: `radial-gradient(ellipse at center, ${themeColor}40 0%, #000 70%)` }}>
      <div className="text-center max-w-3xl">
        <div className="inline-flex items-center justify-center gap-2 px-4 py-1.5 rounded-full mb-8 bg-white/10 border border-white/20">
          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          <span className="text-[10px] uppercase tracking-widest text-white font-bold">PRÉSENTATION • 75 sec</span>
        </div>
        <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight animate-slide-up" style={{ textShadow: "0 4px 30px rgba(0,0,0,0.9)" }}>
          {storyboard.title}
        </h1>
        <p className="text-xl md:text-2xl text-slate-200 mb-10 animate-slide-up" style={{ animationDelay: "200ms" }}>
          {storyboard.subtitle}
        </p>
        <div className="inline-block px-5 py-3 rounded-full mb-10 animate-slide-up" style={{ animationDelay: "350ms", background: themeColor }}>
          <p className="text-sm md:text-base font-bold text-white">{storyboard.tagline}</p>
        </div>
        <div className="animate-slide-up" style={{ animationDelay: "500ms" }}>
          <button onClick={onStart}
            className="group inline-flex items-center gap-3 px-10 py-5 rounded-full bg-white text-slate-950 font-bold text-lg hover:scale-105 transition-all shadow-2xl"
            data-testid="public-start-btn">
            <Play className="w-6 h-6 fill-current group-hover:translate-x-0.5 transition-transform" />
            Lancer la présentation
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-12 uppercase tracking-widest">
          Powered by CMES Pharma Intel
        </p>
      </div>
      <style>{`
        @keyframes fade-in { from { opacity: 0 } to { opacity: 1 } }
        .animate-fade-in { animation: fade-in 500ms ease-out; }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-up { animation: slide-up 700ms cubic-bezier(0.16, 1, 0.3, 1) backwards; }
      `}</style>
    </div>
  );
}

function SceneFrame({ serviceId, scene, themeColor, isPublic }) {
  const baseUrl = isPublic
    ? `${API}/public/videos/image/${serviceId}/${scene.id}`
    : `${API}/superadmin/training-videos/image/${serviceId}/${scene.id}`;
  return (
    <div className="absolute inset-0 flex items-center justify-center animate-scene-enter">
      <img src={baseUrl} alt={scene.title}
        className="absolute inset-0 w-full h-full object-cover animate-ken-burns" loading="eager" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/80" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-transparent" />
      <div className="absolute bottom-32 left-8 md:left-16 max-w-2xl z-10 animate-text-slide-up">
        <div className="inline-block mb-3 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest" style={{ background: themeColor, color: "white" }}>
          {scene.id.replace("scene_", "Scène ")}
        </div>
        <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight" style={{ textShadow: "0 4px 20px rgba(0,0,0,0.8)" }}>
          {scene.title}
        </h2>
        <p className="text-lg md:text-xl text-slate-100 leading-relaxed max-w-xl" style={{ textShadow: "0 2px 10px rgba(0,0,0,0.8)" }}>
          {scene.subtitle}
        </p>
      </div>
      <style>{`
        @keyframes ken-burns {
          0% { transform: scale(1) translate(0, 0); }
          100% { transform: scale(1.08) translate(-1%, -1%); }
        }
        .animate-ken-burns { animation: ken-burns ${(scene.duration_ms || 5500) + 500}ms ease-out forwards; }
        @keyframes scene-enter { from { opacity: 0 } to { opacity: 1 } }
        .animate-scene-enter { animation: scene-enter 600ms ease-out; }
        @keyframes text-slide-up {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-text-slide-up { animation: text-slide-up 700ms cubic-bezier(0.16, 1, 0.3, 1) 200ms backwards; }
      `}</style>
    </div>
  );
}

function FinalCta({ storyboard, onRestart }) {
  const themeColor = storyboard.color_theme;
  return (
    <div className="absolute inset-0 flex items-center justify-center p-8 animate-fade-in overflow-y-auto"
      style={{ background: `radial-gradient(ellipse at center, ${themeColor}30 0%, #000 70%)` }}>
      <div className="text-center max-w-3xl py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
          style={{ background: themeColor }}>
          <CheckCircle className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 animate-slide-up">
          {storyboard.cta.headline}
        </h2>
        <p className="text-xl text-slate-200 mb-10 animate-slide-up" style={{ animationDelay: "200ms" }}>
          {storyboard.cta.body}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-4 animate-slide-up mb-10" style={{ animationDelay: "400ms" }}>
          <a href="mailto:contact@cmes-pharma.org?subject=Démo CMES Pharma Intel"
            className="inline-flex items-center gap-2 text-slate-950 font-bold px-8 py-5 rounded-full hover:scale-105 transition-transform text-base"
            style={{ background: themeColor, color: "white" }}
            data-testid="public-cta-email">
            <Mail className="w-5 h-5" /> {storyboard.cta.button}
          </a>
          <a href="tel:+2250000000000" className="inline-flex items-center gap-2 border border-white/30 text-white hover:bg-white/10 px-6 py-5 rounded-full transition">
            <Phone className="w-4 h-4" /> Appeler
          </a>
          <button onClick={onRestart} className="inline-flex items-center gap-2 border border-white/20 text-slate-300 hover:bg-white/5 px-5 py-5 rounded-full transition">
            <RefreshCw className="w-4 h-4" /> Revoir
          </button>
        </div>
        <p className="text-xs text-slate-500 uppercase tracking-widest">
          CMES Pharma Intel — {storyboard.title}
        </p>
      </div>
    </div>
  );
}
